import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Sortable from "sortablejs";
import submit from "submit-form";
import { useRillContext } from "../../../rill/RillReact/share/base";
import { convertUnit } from "../../../utils/converters/convert-units";
import { timeFormat } from "../../../utils/converters/time-helper";
import {
  addOrUpdateExercise,
  buildExerciseOptions,
  createExerciseToAdd,
  createFormSubmissionData,
  formatExerciseDisplay,
  formatLoadString,
  getEmptyExercise,
  METCON_TYPES,
  PARAMETER_OPTIONS,
  populateExerciseMetadata,
  RESULT_MEASUREMENTS,
  validateCurrentExercise,
  validateMetcon,
} from "../../../utils/metcon";
import { SearchDropdown } from "../../inputs/search-dropdown";
import TimeSelector from "../../inputs/time-selector";
import { getExerciseName } from "../../modals/learn-more/blockHelpers";
import SvgIcon from "../../SvgIcon";
import ToolTip from "../../tooltip";

const METCON_PARAM_COMMON_KEY = {
  reps: "reps",
  load: "load",
  time: "time",
  distance: "distance",
  rest: "rest",
  minutes: "minutes",
  freeform: "freeForm",
};

const AddMetcon = ({ date, editExercise }) => {
  const { t } = useTranslation(["coaching-portal", "common"]);
  const { locals } = useRillContext();
  const { activeUserToRequest, activeUser } = locals;
  const editMode = locals.query.editMode;

  // Metcon configuration state
  const [metconData, setMetconData] = useState({
    subType: editMode && editExercise ? editExercise.subType || "" : "",
    customName:
      editMode && editExercise
        ? editExercise.customName || undefined
        : undefined,
    duration: editMode && editExercise ? editExercise.duration || 0 : 0, // Store in seconds
    totalRounds: editMode && editExercise ? editExercise.totalRounds || "" : "",
    structure: editMode && editExercise ? editExercise.structure || "" : "",
    restBetweenRounds:
      editMode && editExercise ? editExercise.restBetweenRounds || 0 : 0, // Store in seconds
    resultMeasurement:
      editMode && editExercise
        ? editExercise.resultMeasurement || "rounds"
        : "rounds",
    ladderDirection:
      editMode && editExercise
        ? editExercise.ladderDirection || "ascending"
        : "ascending",
    ladderIncrement:
      editMode && editExercise ? editExercise.ladderIncrement || 1 : 1,
  });

  // Time selector states
  const [isDurationSelectorOpen, setIsDurationSelectorOpen] = useState(false);
  const [isRestSelectorOpen, setIsRestSelectorOpen] = useState(false);

  // Close time selectors when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".time-selector-container")) {
        setIsDurationSelectorOpen(false);
        setIsRestSelectorOpen(false);
      }
    };

    if (isDurationSelectorOpen || isRestSelectorOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isDurationSelectorOpen, isRestSelectorOpen]);

  // Current exercise being added/edited
  const [currentExercise, setCurrentExercise] = useState(getEmptyExercise());

  // List of exercises in the metcon
  const [exercisesList, setExercisesList] = useState(
    editMode && editExercise?.sets
      ? editExercise.sets.map((set) => {
          // Convert stored kg values to user's preferred units for editing
          const exerciseData = {
            exercise: set.exercise || "",
            primary: set.primary || "",
            type: set.type || "",
            parameters: set.parameters || [],
            reps: set.reps || "",
            time: set.time || "",
            distance: set.distance || "",
            rest: set.rest || "",
            minutes: set.minutes || "",
            freeform: set.freeform || "",
            instructionVideo: set.instructionVideo || null,
            instructions: set.instructions || null,
            primaryMuscleGroup: set.primaryMuscleGroup || null,
            secondaryMuscleGroup: set.secondaryMuscleGroup || null,
            exerciseTypeField: set.exerciseTypeField || null,
          };

          if (set.loadFemale && set.load) {
            const maleLoad =
              activeUser.units === "imperial"
                ? Math.round(
                    convertUnit(parseFloat(set.load), "kg", "lb") * 100
                  ) / 100
                : set.load;
            const femaleLoad =
              activeUser.units === "imperial"
                ? Math.round(
                    convertUnit(parseFloat(set.loadFemale), "kg", "lb") * 100
                  ) / 100
                : set.loadFemale;
            exerciseData.load = maleLoad;
            exerciseData.loadFemale = femaleLoad;
          } else if (set.load) {
            exerciseData.load =
              activeUser.units === "imperial"
                ? Math.round(
                    convertUnit(parseFloat(set.load), "kg", "lb") * 100
                  ) / 100
                : set.load;
          }

          return exerciseData;
        })
      : []
  );

  const setListRef = useRef(null);
  const sortableRef = useRef(null);

  // Editing state
  const [editingIndex, setEditingIndex] = useState(null);
  const [notes, setNotes] = useState(
    editMode && editExercise ? editExercise.notes || "" : ""
  );

  useEffect(() => {
    const setList = document.querySelector(".set-list");
    if (setList) {
      setList.scrollTop = setList.scrollHeight;
    }
  }, [exercisesList.length]);

  useEffect(() => {
    if (!setListRef.current || exercisesList.length === 0) return;
    const dragImage = document.createElement("img");
    dragImage.src =
      "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
    sortableRef.current = Sortable.create(setListRef.current, {
      animation: 200,
      handle: ".exercise-drag-handle",
      draggable: ".exercise-row",
      disabled: editingIndex !== null,
      setData(dataTransfer) {
        dataTransfer.setDragImage(dragImage, 0, 0);
      },
      onEnd(e) {
        if (e.oldIndex === e.newIndex) return;
        // Revert DOM — let React own the list
        const items = e.from.querySelectorAll(":scope > .exercise-row");
        e.from.insertBefore(
          e.item,
          items[e.oldIndex + (e.oldIndex > e.newIndex)]
        );
        setExercisesList((prev) => {
          const next = [...prev];
          const [moved] = next.splice(e.oldIndex, 1);
          next.splice(e.newIndex, 0, moved);
          return next;
        });
      },
    });
    return () => {
      sortableRef.current?.destroy();
      sortableRef.current = null;
      dragImage.remove();
    };
  }, [exercisesList.length]);

  useEffect(() => {
    if (!sortableRef.current) return;
    sortableRef.current.option("disabled", editingIndex !== null);
  }, [editingIndex]);

  // Build exercise options from library
  const exerciseOptions = useMemo(() => {
    return buildExerciseOptions(activeUserToRequest.exerciseLibrary);
  }, [activeUserToRequest]);

  const handleMetconConfigChange = (e) => {
    const { name, value } = e.target;
    setMetconData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTimeChange = (field, valueInSeconds) => {
    setMetconData((prev) => ({
      ...prev,
      [field]: valueInSeconds,
    }));
  };

  const handleCurrentExerciseChange = (e) => {
    const { name, value } = e.target;
    setCurrentExercise((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleParameterToggle = (parameter) => {
    setCurrentExercise((prev) => {
      const parameters = prev.parameters.includes(parameter)
        ? prev.parameters.filter((p) => p !== parameter)
        : [...prev.parameters, parameter];
      return { ...prev, parameters };
    });
  };

  const handleAddExercise = () => {
    if (!validateCurrentExercise(currentExercise, locals)) {
      return;
    }

    const exerciseToAdd = createExerciseToAdd(currentExercise);
    const { updatedList, newEditingIndex } = addOrUpdateExercise(
      exercisesList,
      exerciseToAdd,
      editingIndex
    );

    setExercisesList(updatedList);
    setEditingIndex(newEditingIndex);
    setCurrentExercise(getEmptyExercise());
  };

  const handleEditExercise = (index) => {
    const exerciseToEdit = exercisesList[index];

    let loadDisplayValue = "";
    if (exerciseToEdit.loadFemale && exerciseToEdit.load) {
      loadDisplayValue = formatLoadString(
        exerciseToEdit.load,
        exerciseToEdit.loadFemale
      );
    } else if (exerciseToEdit.load) {
      loadDisplayValue = exerciseToEdit.load.toString();
    }

    setCurrentExercise({
      exercise: exerciseToEdit.exercise,
      primary: exerciseToEdit.primary || "",
      type: exerciseToEdit.type || "",
      parameters: [...exerciseToEdit.parameters],
      reps: exerciseToEdit.reps || "",
      load: loadDisplayValue,
      time: exerciseToEdit.time || "",
      distance: exerciseToEdit.distance || "",
      rest: exerciseToEdit.rest || "",
      minutes: exerciseToEdit.minutes || "",
      freeform: exerciseToEdit.freeform || "",
      instructionVideo: exerciseToEdit.instructionVideo || null,
      instructions: exerciseToEdit.instructions || null,
      primaryMuscleGroup: exerciseToEdit.primaryMuscleGroup || null,
      secondaryMuscleGroup: exerciseToEdit.secondaryMuscleGroup || null,
      exerciseTypeField: exerciseToEdit.exerciseTypeField || null,
    });
    setEditingIndex(index);
  };

  const handleDeleteExercise = (index) => {
    if (editingIndex === index) {
      setEditingIndex(null);
      setCurrentExercise(getEmptyExercise());
    } else {
      const updatedList = exercisesList.filter((_, i) => i !== index);
      setExercisesList(updatedList);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateMetcon(metconData, exercisesList, locals)) {
      return;
    }

    const exercisesListForSubmission = exercisesList.map((exercise) => {
      const exerciseCopy = { ...exercise };

      if (exerciseCopy.load) {
        exerciseCopy.load =
          activeUser.units === "imperial"
            ? Math.round(
                convertUnit(parseFloat(exerciseCopy.load), "lb", "kg") * 100
              ) / 100
            : parseFloat(exerciseCopy.load);
      }

      if (exerciseCopy.loadFemale) {
        exerciseCopy.loadFemale =
          activeUser.units === "imperial"
            ? Math.round(
                convertUnit(parseFloat(exerciseCopy.loadFemale), "lb", "kg") *
                  100
              ) / 100
            : parseFloat(exerciseCopy.loadFemale);
      }

      return exerciseCopy;
    });

    const form = createFormSubmissionData(
      metconData,
      exercisesListForSubmission,
      notes,
      locals,
      date,
      editMode ? editExercise : null
    );

    const saveUrl =
      editMode && editExercise
        ? `/actions/exercise/save/${editExercise._id}?proxyAthlete=${locals.query.athlete}`
        : `/actions/exercise/save?proxyAthlete=${locals.query.athlete}`;

    submit(saveUrl, {
      method: "POST",
      body: {
        ...form,
        organizationCoach: activeUserToRequest?._id,
      },
    });
  };

  const formatExerciseDisplayJSX = (exercise) => {
    // Pass alreadyConverted=true because exercisesList state stores loads in user's preferred units
    const formatted = formatExerciseDisplay(exercise, locals, true);
    return formatted ? <p className="p4">{formatted}</p> : null;
  };

  const metconTypeOptions = useMemo(
    () =>
      METCON_TYPES.map((type) => ({
        ...type,
        label: t(`addMetcon.metconTypes.${type.value}`),
      })),
    [t]
  );

  return (
    <form onSubmit={handleSubmit} id="add-metcon">
      <div className="body">
        {/* Top section: Type, Duration, Rounds, Structure */}
        <div className="form-row gap-2">
          <div
            className={`form-row-item basis-${
              metconData.customName !== undefined ? 3 : 4
            }/12`}
          >
            <label>{t("addMetcon.selectType")}</label>
            <SearchDropdown
              options={metconTypeOptions}
              placeholder={t("addMetcon.selectType")}
              handleChange={(selectedOption) => {
                if (selectedOption) {
                  setMetconData((prev) => ({
                    ...prev,
                    subType: selectedOption.value,
                  }));
                }
              }}
              value={
                metconTypeOptions.find(
                  (type) => type.value === metconData.subType
                )?.label || ""
              }
              valueKey="label"
            />
            {metconData.customName === undefined && (
              <button
                type="button"
                data-button-type="secondary"
                onClick={() => {
                  setMetconData((prev) => ({
                    ...prev,
                    customName: "",
                  }));
                }}
                data-icon
                className="parameter-button custom-name"
              >
                <SvgIcon alt="add" src="/img/new-icons/add.svg" />{" "}
                {t("addMetcon.customName")}
              </button>
            )}
          </div>

          {metconData.customName !== undefined && (
            <div className="form-row-item basis-3/12">
              <label htmlFor="customName">{t("addMetcon.customName")}</label>
              <input
                type="text"
                className="form-input w-full h-12"
                placeholder={t("addMetcon.enterCustomName")}
                name="customName"
                value={metconData.customName}
                onChange={handleMetconConfigChange}
              />
              <button
                type="button"
                data-button-type="secondary"
                onClick={() => {
                  setMetconData((prev) => ({
                    ...prev,
                    customName: undefined,
                  }));
                }}
                data-icon
                className="parameter-button custom-name text-(--danger)!"
              >
                <SvgIcon
                  alt="add"
                  src="/img/new-icons/red-cross-20px.svg"
                  noFilterOnLight
                />{" "}
                {t("common:remove")}
              </button>
            </div>
          )}

          <div className="form-row-item basis-2/12">
            <label>{t("addMetcon.duration")}</label>
            <div className="relative time-selector-container">
              <input
                type="text"
                value={
                  metconData.duration > 0 ? timeFormat(metconData.duration) : ""
                }
                placeholder={t("addMetcon.enterTime")}
                className="form-input w-full h-12 pr-10 cursor-pointer"
                readOnly
                onClick={() => setIsDurationSelectorOpen(true)}
              />
              <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                <SvgIcon src="/img/svg/time.svg" />
              </div>
              {isDurationSelectorOpen && (
                <div className="absolute top-8 right-0 z-50">
                  <TimeSelector
                    restTime={metconData.duration}
                    onChange={(e) => {
                      handleTimeChange("duration", e.target.value);
                      setIsDurationSelectorOpen(false);
                    }}
                    initialOpen={true}
                    label={t("addMetcon.metconDuration")}
                  />
                </div>
              )}
            </div>
            <ToolTip
              id="duration-tooltip"
              className="absolute top-[-0.35rem] right-0"
              html={t("addMetcon.durationTooltip")}
            >
              <SvgIcon
                alt={t("addMetcon.infoIconAlt")}
                className="cursor-pointer"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>

          <div className="form-row-item basis-2/12">
            <label htmlFor="totalRounds">{t("addMetcon.rounds")}</label>
            <input
              type="number"
              name="totalRounds"
              value={metconData.totalRounds}
              onChange={handleMetconConfigChange}
              onInvalid={(e) => {
                e.target.setCustomValidity(
                  t("addMetcon.singleNumberValidation")
                );
              }}
              onInput={(e) => {
                e.target.setCustomValidity("");
              }}
              placeholder={t("addMetcon.enterNumber")}
              className="form-input w-full h-12"
            />
            <ToolTip
              id="rounds-tooltip"
              className="absolute top-[-0.35rem] right-0"
              html={t("addMetcon.roundsTooltip")}
            >
              <SvgIcon
                alt={t("addMetcon.infoIconAlt")}
                className="cursor-pointer"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>

          <div
            className={`form-row-item basis-${
              metconData.customName !== undefined ? 4 : 5
            }/12`}
          >
            <label htmlFor="structure">{t("addMetcon.structure")}</label>
            <input
              type="text"
              name="structure"
              value={metconData.structure}
              onChange={handleMetconConfigChange}
              placeholder={t("addMetcon.enterProtocol")}
              className="form-input w-full h-12"
            />
            <ToolTip
              id="structure-tooltip"
              className="absolute top-[-0.35rem] right-0"
              html={t("addMetcon.structureTooltip")}
            >
              <SvgIcon
                alt={t("addMetcon.infoIconAlt")}
                className="cursor-pointer"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>
        </div>

        {/* Ladder-specific configuration */}
        {metconData.subType === "ladder" && (
          <div className="form-row gap-2 mt-4">
            <div className="form-row-item basis-4/12">
              <label htmlFor="ladderDirection">
                {t("addMetcon.ladderDirection")}
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setMetconData({
                      ...metconData,
                      ladderDirection: "ascending",
                    })
                  }
                  data-button-type={
                    metconData.ladderDirection === "ascending"
                      ? "outline"
                      : "text-white"
                  }
                >
                  {t("addMetcon.ascending")}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setMetconData({
                      ...metconData,
                      ladderDirection: "descending",
                    })
                  }
                  data-button-type={
                    metconData.ladderDirection === "descending"
                      ? "outline"
                      : "text-white"
                  }
                >
                  {t("addMetcon.descending")}
                </button>
              </div>
            </div>

            <div className="form-row-item basis-3/12">
              <label>
                {metconData.ladderDirection === "ascending"
                  ? t("addMetcon.increment")
                  : t("addMetcon.decrement")}
              </label>
              <input
                type="number"
                name="ladderIncrement"
                value={metconData.ladderIncrement}
                onChange={handleMetconConfigChange}
                placeholder="1"
                min="1"
                className="form-input w-full h-12"
              />
            </div>

            <div className="form-row-item basis-5/12">
              <label className="mt-4 mb-0!">
                {metconData.ladderDirection === "ascending"
                  ? t("addMetcon.repsIncreaseBy", {
                      increment: metconData.ladderIncrement,
                    })
                  : t("addMetcon.repsDecreaseBy", {
                      decrement: metconData.ladderIncrement,
                    })}
              </label>
            </div>
          </div>
        )}

        <div className="group alt-add-sets">
          {/* Exercise List or No Exercises Message */}
          {exercisesList.length > 0 ? (
            <div className="set-list" ref={setListRef}>
              {exercisesList.map((exercise, index) => (
                <div
                  key={index}
                  className={`exercise-row flex items-center justify-between px-4 py-2 mb-2 rounded-lg ${
                    editingIndex === null || editingIndex === index
                      ? "bg-(--form-element-alt-background-color)"
                      : "bg-transparent"
                  }`}
                >
                  <span
                    className={`exercise-drag-handle cursor-grab mr-2 shrink-0 flex items-center ${editingIndex !== null ? "invisible" : ""}`}
                  >
                    <SvgIcon src="/img/new-icons/menu.svg" />
                  </span>
                  <div className="flex items-center gap-8 flex-1">
                    <p className="p4 min-w-24">
                      {getExerciseName({
                        primary: exercise.primary,
                        secondary: exercise.exercise,
                        type: exercise.type,
                      })}
                    </p>
                    <div className="flex items-center gap-4 flex-1">
                      {formatExerciseDisplayJSX(exercise)}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-button-type="secondary"
                      onClick={() => handleEditExercise(index)}
                      className={editingIndex === null ? "" : "invisible"}
                    >
                      <SvgIcon src="/img/new-icons/edit-pencil.svg" />
                    </button>
                    <button
                      type="button"
                      data-button-type="secondary"
                      onClick={() => handleDeleteExercise(index)}
                      className={
                        editingIndex === null || editingIndex === index
                          ? ""
                          : "invisible"
                      }
                    >
                      <SvgIcon
                        noFilterOnLight
                        src={
                          editingIndex === index
                            ? "/img/new-icons/red-cross.svg"
                            : "/img/new-icons/red-trash.svg"
                        }
                      />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="set-list no-sets">
              <p className="s1">{t("addMetcon.noExercises")}</p>
              <p className="p4 mt-2">{t("addMetcon.addFirstExerciseBelow")}</p>
            </div>
          )}

          <div className="add-set-wrapper">
            {/* Exercise Builder: Exercise name + parameter inputs + save button */}
            <div className="form-row gap-2 mb-4 flex-1 items-end">
              <div className="form-row-item flex-1">
                <label>{t("addMetcon.exerciseName")}</label>
                <SearchDropdown
                  options={exerciseOptions}
                  placeholder={t("addMetcon.selectExercise")}
                  handleChange={(val) => {
                    if (val) {
                      const exerciseWithBasicInfo = {
                        ...getEmptyExercise(),
                        exercise: val.value,
                        primary: val.primary,
                        type: val.type,
                      };

                      // Populate metadata from exerciseLibrary
                      const exerciseWithMetadata = populateExerciseMetadata(
                        exerciseWithBasicInfo,
                        activeUserToRequest.exerciseLibrary,
                        activeUserToRequest.primaryExercises
                      );

                      setCurrentExercise(exerciseWithMetadata);
                    }
                  }}
                  value={currentExercise.exercise}
                />
              </div>

              {/* Parameter Input Fields */}
              {currentExercise.parameters.map((param) => (
                <div key={param} className="form-row-item basis-1/12">
                  <label>
                    {t(
                      `common:parameterOptions.${METCON_PARAM_COMMON_KEY[param]}`
                    )}
                    {param === "load"
                      ? ` (${activeUser.units === "imperial" ? "lb" : "kg"})`
                      : ""}
                  </label>
                  <input
                    type={
                      ["reps", "minutes"].includes(param) ? "number" : "text"
                    }
                    name={param}
                    value={currentExercise[param]}
                    onChange={handleCurrentExerciseChange}
                    placeholder={
                      param === "load"
                        ? t("addMetcon.loadPlaceholder")
                        : t("addMetcon.enterValue")
                    }
                    className="form-input w-full h-12!"
                  />
                </div>
              ))}

              {currentExercise.parameters.length === 0 && (
                <div className="chip info h-10">
                  <span className="text-xssm">
                    {t("addMetcon.addAtLeastOneParameter")}
                  </span>
                </div>
              )}

              <div className="form-row-item ml-auto grow-0">
                <button
                  type="button"
                  onClick={handleAddExercise}
                  data-button-type="primary"
                  className="h-10"
                  disabled={
                    !currentExercise.exercise ||
                    currentExercise.parameters.length === 0 ||
                    !currentExercise.parameters.some(
                      (param) =>
                        currentExercise[param] &&
                        currentExercise[param].toString().trim() !== ""
                    )
                  }
                >
                  {editingIndex !== null ? t("common:save") : t("common:add")}
                </button>
              </div>
            </div>
          </div>

          {/* Parameter Toggles - Outside add-set-wrapper */}
          <div className="parameter-selector flex items-center gap-4">
            <p className="p5">{t("addMetcon.selectParameters")}</p>
            <div className="flex flex-wrap gap-2 flex-1">
              {PARAMETER_OPTIONS.map((param) => (
                <button
                  key={param}
                  type="button"
                  data-button-type="secondary"
                  onClick={() => handleParameterToggle(param)}
                  data-icon
                  className="parameter-button"
                  disabled={!currentExercise.exercise}
                >
                  <SvgIcon
                    alt="add"
                    src={
                      currentExercise.parameters.includes(param)
                        ? "/img/new-icons/red-cross-20px.svg"
                        : "/img/new-icons/add.svg"
                    }
                    noFilterOnLight={currentExercise.parameters.includes(param)}
                  />{" "}
                  {t(
                    `common:parameterOptions.${METCON_PARAM_COMMON_KEY[param]}`
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom row: Rest Between Rounds, Result Measurement, Notes */}
        <div className="form-row gap-2">
          <div className="form-row-item basis-3/12">
            <label>{t("addMetcon.restBetweenRounds")}</label>
            <div className="relative time-selector-container">
              <input
                type="text"
                value={
                  metconData.restBetweenRounds > 0
                    ? timeFormat(metconData.restBetweenRounds)
                    : ""
                }
                placeholder={t("addMetcon.selectTime")}
                className="form-input w-full h-12 pr-10 cursor-pointer"
                readOnly
                onClick={() => setIsRestSelectorOpen(true)}
              />
              <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                <SvgIcon src="/img/svg/time.svg" />
              </div>
              {isRestSelectorOpen && (
                <div className="rest-time-popup absolute bottom-full right-0 z-50">
                  <TimeSelector
                    restTime={metconData.restBetweenRounds}
                    onChange={(e) => {
                      handleTimeChange("restBetweenRounds", e.target.value);
                      setIsRestSelectorOpen(false);
                    }}
                    initialOpen={true}
                    label={t("addMetcon.restTime")}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="form-row-item basis-3/12">
            <label htmlFor="resultMeasurement">
              {t("addMetcon.resultMeasurement")}
            </label>
            <select
              name="resultMeasurement"
              value={metconData.resultMeasurement}
              onChange={handleMetconConfigChange}
              className="form-select w-full h-12"
            >
              {RESULT_MEASUREMENTS.map((result) => (
                <option key={result.value} value={result.value}>
                  {t(`addMetcon.resultMeasurementOptions.${result.value}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="form-row-item basis-6/12">
            <label htmlFor="notes">{t("common:notes")}</label>
            <textarea
              tabIndex="0"
              name="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("addMetcon.notesPlaceholder")}
              className="form-textarea w-full h-12! resize-none"
            />
          </div>
        </div>
      </div>

      <footer>
        <div className="invisible" />
        <div className="flex gap-2">
          <button
            data-button-type="secondary"
            onClick={(e) => {
              e.preventDefault();
              submit(`/actions/refresh/`, {
                method: "GET",
                body: { modal: "", editMode: "", addType: "" },
              });
            }}
            className="text-(--danger)!"
            type="button"
          >
            {t("common:cancel")}
          </button>
          <button
            data-button-type="primary"
            type="submit"
            disabled={exercisesList.length === 0 || !metconData.subType}
          >
            {t("common:save")}
          </button>
        </div>
      </footer>
    </form>
  );
};

AddMetcon.propTypes = {
  date: PropTypes.object,
  editExercise: PropTypes.object,
};

export default AddMetcon;
